import { AuthContext, timestampByUTC, uuid } from '@repo-level/core-front';
import type { EventWithPayload } from 'api/analytics';
import { pushAnalytics } from 'api/analytics';
import { useDeepCompareMemo } from 'hooks/common';
import produce from 'immer';
import { useCallback, useContext } from 'react';

import { LocalStorage } from './localStorage';

interface AnalyticsStoreI {
  sessionId: string;
  createdAt: number;
}

const AnalyticsStore = new LocalStorage<AnalyticsStoreI, null>(
  'analytics',
  null
);

export const getSessionId = () => {
  const analyticsStore = AnalyticsStore.get();
  const createdAt = new Date().getTime();

  const searchParams = new URLSearchParams(window.location.search);
  const sessionIdParam = searchParams.get('session_id');

  if (!analyticsStore || sessionIdParam) {
    const sessionId =
      sessionIdParam && sessionIdParam !== 'null' ? sessionIdParam : uuid();

    AnalyticsStore.replace({ sessionId, createdAt });
    return sessionId;
  }

  return analyticsStore.sessionId;
};

export const sendEventToAnalyticsFn = (
  event: EventWithPayload,
  userId?: number
) => {
  const sessionId = getSessionId();

  const userIdT = (() => {
    if (event.attributes?.userId == null) return userId;
    return event.attributes.userId;
  })();

  const setExtraDetails = (
    eventName: string,
    sessionId: string,
    userId: number | undefined
  ) =>
    produce(event, (draft) => {
      draft.name = eventName as any;
      draft.attributes = draft.attributes ?? {};
      draft.attributes.sessionId = sessionId;
      draft.attributes.userId = userId;
      draft.attributes.email = event.attributes?.email;
      draft.attributes.createdAt = timestampByUTC();
      draft.attributes.updatedAt = timestampByUTC();
    });

  try {
    pushAnalytics(setExtraDetails(event.name, sessionId, userIdT));
  } catch (error) {}
};

export const useAnalytics = () => {
  const { user } = useContext(AuthContext);

  const memoizedUserId = useDeepCompareMemo(user?.id);

  const sendEventToAnalytics = useCallback(
    async (event: EventWithPayload) =>
      sendEventToAnalyticsFn(event, memoizedUserId),
    [memoizedUserId]
  );

  return { sendEventToAnalytics };
};
